import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Typography,
  Container,
  Box,
  Divider,
} from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer/Footer';
import InvestorBanner from '../components/InvestorBanner/InvestorBanner';

const TermsAndConditions = () => {
  const footerRef = useRef(null);
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const handleContactUsClick = () => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const SectionTitle = ({ children }) => (
    <Typography 
      sx={{ 
        fontSize: { xs: "16px", md: "18px" }, 
        fontWeight: "600", 
        color: "#28947F",
        mt: 3,
        mb: 1.5
      }}
    >
      {children}
    </Typography>
  );

  const BodyText = ({ children }) => (
    <Typography 
      sx={{ 
        fontSize: { xs: "13px", md: "14px" },
        lineHeight: 1.8,
        color: "#2c2c2c",
        mb: 2,
        textAlign: "justify"
      }}
    >
      {children}
    </Typography>
  );

  const BulletPoint = ({ children }) => (
    <Box sx={{ display: "flex", gap: 1.5, mb: 2, pl: 2 }}>
      <Box 
        sx={{ 
          minWidth: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#28947F",
          mt: 1
        }}
      />
      <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, lineHeight: 1.8, color: "#2c2c2c", textAlign: "justify" }}>
        {children}
      </Typography>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        display: "flex", 
        flexDirection: "column", 
        background: `linear-gradient(90deg, rgba(209,230,226,1) 0%, rgba(234,238,236,1) 95%)`,
      }}
    >
      <Helmet>
        <title>Terms & Conditions - InvestoFarms</title>
        <meta name="description" content="Review our Terms & Conditions for using the InvestoFarms website and services. Understand guidelines and responsibilities." />
      </Helmet>

      <Navbar onContactUsClick={handleContactUsClick} />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
          {/* Header */}
          <Typography 
            sx={{ 
              fontSize: { xs: "26px", md: "34px" }, 
              fontWeight: "700", 
              color: "#28947F", 
              textAlign: "center",
              mb: 4
            }}
          >
            Terms & Conditions
          </Typography>

          {/* Introduction */}
          <BodyText>
            Welcome to the InvestoFarms website, which is owned and administered by InvestoFarms Partners Private Limited ("InvestoFarms," "we," "us," or "our"). You accept and agree to be governed by the following terms and conditions of usage (the "Terms of Use") by accessing or using the Site. Before using the Site, please carefully read these Terms of Use.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Acceptance of Conditions */}
          <SectionTitle>1. Acceptance of the Conditions</SectionTitle>
          <BodyText>
            You accept that these Terms of Use, along with any applicable laws and regulations, will apply to you when you access or use the Site. It is forbidden for you to use or access this Site if you disagree with any of these terms. The content on this website is shielded by relevant trademark and copyright laws.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Use Licence */}
          <SectionTitle>2. Use Licence</SectionTitle>
          <BodyText>
            Permission is given to download one copy of the content (software or information) from the InvestoFarms website, operated by InvestoFarms Partners Private Limited, for short-term, personal, non-commercial reading only. This is merely a licence grant, not a title transfer.
          </BodyText>
          
          <BodyText>As a result, you are not permitted to:</BodyText>
          
          <Box sx={{ mb: 3 }}>
            <BulletPoint>Alter or duplicate the materials;</BulletPoint>
            <BulletPoint>Use them for any kind of commercial endeavour or public exhibition, whether for profit or not;</BulletPoint>
            <BulletPoint>Make an effort to reverse engineer or decompile any software found on the InvestoFarms Partners Private Limited website, also referred to as InvestoFarms;</BulletPoint>
            <BulletPoint>Either remove any proprietary or copyright notices from the materials, or transfer them to another individual or "mirror" them on a different server.</BulletPoint>
          </Box>

          <BodyText>
            This licence will instantly expire if you break any of these guidelines, and InvestoFarms Partners Private Limited (hereinafter referred to as "InvestoFarms") reserves the right to do so at any moment. You shall destroy any downloaded materials in your possession, whether in printed or electronic format, upon ending your viewing of these materials or upon the termination of this licence.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Notice of Disclaimer */}
          <SectionTitle>3. Notice of Disclaimer</SectionTitle>
          <BodyText>
            The information on the InvestoFarms Partners Private Limited website (known as "InvestoFarms") is provided "as is." No warranties are made by InvestoFarms Partners Private Limited, either express or implied. All other warranties, including implied warranties or conditions of merchantability, fitness for a particular purpose, and non-infringement of intellectual property or other rights violations, are hereby disclaimed and negated.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Restrictions */}
          <SectionTitle>4. Restrictions</SectionTitle>
          <BodyText>
            Even though InvestoFarms Partners Private Limited (known as InvestoFarms) or an authorised representative of InvestoFarms has been informed orally or in writing of the possibility of such damage, InvestoFarms Partners Private Limited or its suppliers shall in no case be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on InvestoFarms' Site.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Corrections and Updates */}
          <SectionTitle>5. Corrections and Updates</SectionTitle>
          <BodyText>
            There may be typographical, technical, or photographic errors in the contents that appear on the InvestoFarms website. None of the content on InvestoFarms website, operated by InvestoFarms Partners Private Limited (hereinafter referred to as "InvestoFarms"), is guaranteed to be true, comprehensive, or up to date. InvestoFarms reserves the right, at any moment and without prior notice, to modify the content on its website. However, InvestoFarms makes no promises regarding updating the content.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Links */}
          <SectionTitle>6. Links</SectionTitle>
          <BodyText>
            InvestoFarms Partners Private Limited (commonly known as InvestoFarms) disclaims any liability for the contents of any linked website, as it has not examined all of the websites that link to its website. Any link does not indicate that InvestoFarms Partners Private Limited approves of the website. The user assumes all risk while using any such linked website.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Modifications */}
          <SectionTitle>7. Modifications to the Site Terms of Use</SectionTitle>
          <BodyText>
            These Terms of Use for this Site may be changed by InvestoFarms Partners Private Limited (doing business as InvestoFarms) at any time and without prior notice. By using this site, you accept to be legally bound by the most recent version of these Terms of Use.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Regulation of Law */}
          <SectionTitle>8. Regulation of Law and Authority</SectionTitle>
          <BodyText>
            The laws of India shall govern and be applied to the interpretation of these Terms of Use. Ambala, Haryana, India courts will have exclusive jurisdiction over any dispute arising out of or relating to these Terms of Use.
          </BodyText>

          {/* Contact Section */}
          <Box sx={{ mt: 4, p: 3, backgroundColor: "rgba(255, 255, 255, 0.5)", borderRadius: 2, border: "1px solid rgba(40, 148, 127, 0.3)" }}>
            <Typography sx={{ fontSize: { xs: "15px", md: "16px" }, fontWeight: "600", color: "#28947F", mb: 1 }}>
              Get in Touch with Us
            </Typography>
            <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: "#333" }}>
              Please send an email to <strong>support@investofarms.com</strong> with any questions you may have regarding our terms of use.
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box sx={{ mt: "auto" }}>
        <InvestorBanner />
        <Footer ref={footerRef} />
      </Box>
    </Box>
  );
};

export default TermsAndConditions;